import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { createJob, publishJob, getJob, updateJob } from '@/api/jobs';
import { listMyClasses } from '@/api/classes';
import { Button } from '@/components/ui/button/button';
import { Card } from '@/components/ui/card/card';
import { Input } from '@/components/ui/input/input';
import type { DimensionDef, JobType, Question, QuestionType } from '@/types';

import styles from './create-job.module.css';


const TYPE_OPTIONS: { value: JobType; label: string; group: string }[] = [
  { value: 'essay', label: '作文 / 主观题', group: '语文' },
  { value: 'reading_chinese', label: '语文阅读', group: '语文' },
  { value: 'poetry', label: '古诗文默写', group: '语文' },
  { value: 'math', label: '数学', group: '数学' },
  { value: 'geometry', label: '几何证明', group: '数学' },
  { value: 'word_problem', label: '数学应用题', group: '数学' },
  { value: 'english_writing', label: '英语作文', group: '英语' },
  { value: 'english_reading', label: '英语阅读', group: '英语' },
  { value: 'cloze', label: '完形填空', group: '英语' },
  { value: 'code', label: '代码 / 编程', group: '信息技术' },
  { value: 'physics', label: '物理', group: '理科' },
  { value: 'chemistry', label: '化学', group: '理科' },
  { value: 'biology', label: '生物', group: '理科' },
  { value: 'history', label: '历史', group: '文科' },
  { value: 'geography', label: '地理', group: '文科' },
  { value: 'politics', label: '政治', group: '文科' },
  { value: 'choice', label: '选择题（客观题·自动批改）', group: '客观题' },
  { value: 'fill_blank', label: '填空题（客观题·自动批改）', group: '客观题' },
];

const DEFAULT_DIMENSIONS: DimensionDef[] = [
  { name: '内容完整性', max_score: 40, weight: 0.4 },
  { name: '语言表达', max_score: 30, weight: 0.3 },
  { name: '结构逻辑', max_score: 20, weight: 0.2 },
  { name: '书写规范', max_score: 10, weight: 0.1 },
];

function recommendedDimensions(type: JobType, total: number): DimensionDef[] {
  const names: Record<JobType, string[]> = {
    essay: ['内容完整性', '语言表达', '结构逻辑', '书写规范'],
    reading_chinese: ['理解准确', '答题完整', '分析深入', '语言规范'],
    poetry: ['默写准确', '理解赏析', '书写规范'],
    math: ['解题思路', '计算准确', '步骤完整', '答案正确'],
    geometry: ['已知条件', '推理严谨', '作图规范', '结论正确'],
    word_problem: ['审题分析', '建模列式', '计算准确', '作答完整'],
    english_writing: ['内容要点', '语法词汇', '篇章结构', '书写规范'],
    english_reading: ['理解准确', '答题完整', '推理判断', '词汇运用'],
    cloze: ['语义理解', '语法运用', '词汇辨析', '上下文逻辑'],
    code: ['功能正确', '代码规范', '算法效率', '可读性'],
    physics: ['概念理解', '分析过程', '计算准确', '表述规范'],
    chemistry: ['概念理解', '方程式正确', '计算准确', '实验分析'],
    biology: ['概念准确', '图表分析', '实验推理', '术语规范'],
    history: ['史实准确', '分析全面', '逻辑清晰', '表述规范'],
    geography: ['读图能力', '知识准确', '综合分析', '表述规范'],
    politics: ['知识准确', '观点明确', '论证充分', '表述规范'],
    choice: ['内容完整性', '语言表达', '结构逻辑', '书写规范'],
    fill_blank: ['内容完整性', '语言表达', '结构逻辑', '书写规范'],
  };
  const list = names[type] ?? names.essay;
  const count = list.length;
  const base = Math.floor(total / count);
  const remainder = total - base * count;
  return list.map((name, idx) => ({
    name,
    max_score: base + (idx < remainder ? 1 : 0),
    weight: Number((1 / count).toFixed(4)),
  }));
}

const isObjective = (t: JobType) => t === 'choice' || t === 'fill_blank';

function letterOf(i: number): string {
  return String.fromCharCode(65 + i);
}

export function CreateJobPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const editJobId = id ? Number(id) : null;
  const isEdit = editJobId !== null && !Number.isNaN(editJobId);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [subject, setSubject] = useState('语文');
  const [type, setType] = useState<JobType>('essay');
  const [maxScore, setMaxScore] = useState('100');
  const [dimensions, setDimensions] = useState<DimensionDef[]>(DEFAULT_DIMENSIONS);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [classId, setClassId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [referenceAnswer, setReferenceAnswer] = useState('');
  const [showReference, setShowReference] = useState(false);
  const [autoPublish, setAutoPublish] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formLoaded, setFormLoaded] = useState(false);

  const { data: classes } = useQuery({
    queryKey: ['my-classes'],
    queryFn: () => listMyClasses(),
  });

  const { data: jobDetail } = useQuery({
    queryKey: ['job', editJobId],
    queryFn: () => getJob(editJobId!),
    enabled: isEdit,
  });

  // 编辑模式：加载作业后预填表单
  useEffect(() => {
    if (!isEdit || !jobDetail || formLoaded) return;
    const j = jobDetail.job;
    setTitle(j.title);
    setDescription(j.description ?? '');
    setSubject(j.subject ?? '语文');
    setType(j.type);
    setMaxScore(String(j.max_score));
    setDimensions(j.dimensions?.length ? j.dimensions : DEFAULT_DIMENSIONS);
    setQuestions(j.questions ?? []);
    setClassId(j.class_id ? String(j.class_id) : '');
    if (j.due_date) {
      const d = new Date(j.due_date);
      const pad = (n: number) => String(n).padStart(2, '0');
      setDueDate(
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`,
      );
    }
    setReferenceAnswer(j.reference_answer ?? '');
    setShowReference(!!j.show_reference);
    setFormLoaded(true);
  }, [isEdit, jobDetail, formLoaded]);

  // 已有学生提交：锁定评分口径字段（题型 / 题目 / 评分维度 / 满分）
  const locked = isEdit && (jobDetail?.submitted_count ?? 0) > 0;


  const objective = isObjective(type);

  const totalMaxScore = useMemo(
    () => dimensions.reduce((sum, d) => sum + d.max_score, 0),
    [dimensions],
  );
  const totalWeight = useMemo(
    () => Number(dimensions.reduce((sum, d) => sum + d.weight, 0).toFixed(4)),
    [dimensions],
  );
  const questionsTotal = useMemo(
    () => questions.reduce((sum, q) => sum + (Number(q.score) || 0), 0),
    [questions],
  );

  const updateDimension = (index: number, patch: Partial<DimensionDef>) => {
    setDimensions((prev) =>
      prev.map((d, i) => (i === index ? { ...d, ...patch } : d)),
    );
  };

  const removeDimension = (index: number) => {
    setDimensions((prev) => prev.filter((_, i) => i !== index));
  };

  const addDimension = () => {
    setDimensions((prev) => [
      ...prev,
      { name: `维度 ${prev.length + 1}`, max_score: 0, weight: 0 },
    ]);
  };

  const equalizeWeights = () => {
    const count = dimensions.length;
    if (count === 0) return;
    const weight = Number((1 / count).toFixed(4));
    const last = Number((1 - weight * (count - 1)).toFixed(4));
    setDimensions((prev) =>
      prev.map((d, i) => ({ ...d, weight: i === count - 1 ? last : weight })),
    );
  };

  const applyRecommended = () => {
    const total = objective ? questionsTotal || 100 : Number(maxScore) || 100;
    setDimensions(recommendedDimensions(type, total));
  };

  const validateDimensions = (): string | null => {
    if (dimensions.length === 0) return '请至少添加一个评分维度';
    for (const d of dimensions) {
      if (!d.name.trim()) return '维度名称不能为空';
      if (d.max_score < 0) return '维度满分不能为负数';
      if (d.weight < 0 || d.weight > 1) return '权重必须在 0 ~ 1 之间';
    }
    if (totalWeight < 0.9999 || totalWeight > 1.0001) {
      return `权重之和必须等于 1（当前 ${totalWeight.toFixed(2)}）`;
    }
    if (totalMaxScore !== Number(maxScore)) {
      return `各维度满分之和必须等于作业满分（当前 ${totalMaxScore} / ${maxScore}）`;
    }
    return null;
  };

  const validateQuestions = (): string | null => {
    if (questions.length === 0) return '请至少添加一道题目';
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      const no = i + 1;
      if (!q.stem.trim()) return `第 ${no} 题题干不能为空`;
      if (!(Number(q.score) > 0)) return `第 ${no} 题分值必须大于 0`;
      if (q.type === 'single_choice' || q.type === 'multiple_choice') {
        if ((q.options?.length ?? 0) < 2) return `第 ${no} 题选项至少需要 2 个`;
        if (q.type === 'single_choice') {
          if (!q.answer || typeof q.answer !== 'string') {
            return `第 ${no} 题请设置正确答案`;
          }
        } else if (
          !Array.isArray(q.answer) ||
          (q.answer as string[]).length === 0
        ) {
          return `第 ${no} 题请至少选择一个正确答案`;
        }
      } else {
        if (
          !Array.isArray(q.answer) ||
          (q.answer as string[]).length === 0 ||
          (q.answer as string[]).some((a) => !a.trim())
        ) {
          return `第 ${no} 题请填写每个空的参考答案`;
        }
      }
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (objective && !locked) {
      const qErr = validateQuestions();
      if (qErr) {
        setError(qErr);
        return;
      }
    } else if (!objective && !locked) {
      const dimError = validateDimensions();
      if (dimError) {
        setError(dimError);
        return;
      }
    }


    setLoading(true);
    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description);
    formData.append('subject', subject);
    formData.append('type', type);
    formData.append('max_score', objective ? String(questionsTotal) : maxScore);
    if (objective) {
      formData.append('questions', JSON.stringify(questions));
      formData.append('dimensions', '[]');
    } else {
      formData.append('dimensions', JSON.stringify(dimensions));
      formData.append('questions', '[]');
    }
    if (classId) formData.append('class_id', classId);
    if (dueDate) formData.append('due_date', dueDate);
    if (!objective && referenceAnswer.trim()) {
      formData.append('reference_answer', referenceAnswer.trim());
      formData.append('show_reference', showReference ? 'true' : 'false');
    }

    try {
      if (isEdit) {
        await updateJob(editJobId!, formData);
        await queryClient.invalidateQueries({ queryKey: ['jobs'] });
        await queryClient.invalidateQueries({ queryKey: ['job', editJobId] });
        navigate(`/jobs/${editJobId}`);
      } else {
        const { job_id } = await createJob(formData);
        if (autoPublish) {
          await publishJob(job_id);
        }
        await queryClient.invalidateQueries({ queryKey: ['jobs'] });
        navigate(`/jobs/${job_id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : isEdit ? '保存失败' : '创建失败');
    } finally {
      setLoading(false);
    }
  };


  const groupedTypes = useMemo(() => {
    const map = new Map<string, typeof TYPE_OPTIONS>();
    for (const opt of TYPE_OPTIONS) {
      if (!map.has(opt.group)) map.set(opt.group, []);
      map.get(opt.group)!.push(opt);
    }
    return Array.from(map.entries());
  }, []);

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{isEdit ? '编辑作业' : '新建作业'}</h1>
      {isEdit && !formLoaded && <div className={styles.loading}>加载作业信息中...</div>}
      <Card className={styles.formCard}>
        <form className={styles.form} onSubmit={handleSubmit}>
          {locked && (
            <div className={styles.lockedTip}>
              该作业已有 {jobDetail?.submitted_count} 名学生提交，题型、题目、评分维度与满分已锁定，
              只能修改标题、描述、截止时间等基本信息。
            </div>
          )}
          <Input
            label="作业标题"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
          <div className={styles.row}>
            <Input
              label="学科"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
            {!objective && (
              <Input
                label="满分"
                type="number"
                min={0}
                value={maxScore}
                onChange={(e) => setMaxScore(e.target.value)}
                disabled={locked}
              />
            )}
          </div>
          <div className={styles.row}>
            <label className={styles.selectLabel}>
              类型
              <select
                className={styles.select}
                value={type}
                onChange={(e) => setType(e.target.value as JobType)}
                disabled={locked}
              >

                {groupedTypes.map(([group, options]) => (
                  <optgroup key={group} label={group}>
                    {options.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>
          </div>
          <div className={styles.row}>
            <label className={styles.selectLabel}>
              关联班级（可选）
              <select
                className={styles.select}
                value={classId}
                onChange={(e) => setClassId(e.target.value)}
              >
                <option value="">不关联（所有学生可见）</option>
                {classes?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.selectLabel}>
              截止时间（可选）
              <input
                type="datetime-local"
                className={styles.select}
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </label>
          </div>
          <label className={styles.textareaLabel}>
            作业描述
            <textarea
              className={styles.textarea}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
            />
          </label>

          {!objective && (
            <label className={styles.textareaLabel}>
              参考答案 / 参考范文（可选，学生端仅在开放后可见）
              <textarea
                className={styles.textarea}
                value={referenceAnswer}
                onChange={(e) => setReferenceAnswer(e.target.value)}
                rows={4}
                placeholder="填写标准答案要点或范文，供批阅参考与学生复习使用"
              />
            </label>
          )}
          {!objective && referenceAnswer.trim() && (
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={showReference}
                onChange={(e) => setShowReference(e.target.checked)}
                className={styles.checkbox}
              />
              向学生开放参考答案（也可稍后在作业详情中开放）
            </label>
          )}

          {objective ? (
            <QuestionEditor
              jobType={type}
              questions={questions}
              onChange={setQuestions}
              total={questionsTotal}
              readOnly={locked}
            />
          ) : (

            <div className={styles.dimensionsSection}>
              <div className={styles.dimensionsHeader}>
                <label className={styles.dimensionsTitle}>评分维度</label>
                <div className={styles.dimensionsMeta}>
                  <span
                    className={
                      totalWeight === 1 ? styles.metaOk : styles.metaWarn
                    }
                  >
                    权重合计 {totalWeight.toFixed(2)}
                  </span>
                  <span
                    className={
                      totalMaxScore === Number(maxScore)
                        ? styles.metaOk
                        : styles.metaWarn
                    }
                  >
                    满分合计 {totalMaxScore} / {maxScore}
                  </span>
                </div>
              </div>

              <div className={styles.dimensionsList}>
                {dimensions.map((d, idx) => (
                  <div key={idx} className={styles.dimensionItem}>
                    <input
                      type="text"
                      className={styles.dimInput}
                      value={d.name}
                      onChange={(e) =>
                        updateDimension(idx, { name: e.target.value })
                      }
                      placeholder="维度名称"
                      aria-label="维度名称"
                      disabled={locked}
                    />
                    <input
                      type="number"
                      min={0}
                      className={styles.dimNumber}
                      value={d.max_score}
                      onChange={(e) =>
                        updateDimension(idx, {
                          max_score: Number(e.target.value),
                        })
                      }
                      placeholder="满分"
                      aria-label="满分"
                      disabled={locked}
                    />
                    <input
                      type="number"
                      min={0}
                      max={1}
                      step={0.01}
                      className={styles.dimNumber}
                      value={d.weight}
                      onChange={(e) =>
                        updateDimension(idx, {
                          weight: Number(e.target.value),
                        })
                      }
                      placeholder="权重"
                      aria-label="权重"
                      disabled={locked}
                    />
                    <button
                      type="button"
                      className={styles.dimRemove}
                      onClick={() => removeDimension(idx)}
                      aria-label="删除维度"
                      title="删除维度"
                      disabled={locked}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <div className={styles.dimensionsActions}>
                <Button type="button" variant="secondary" size="small" onClick={addDimension} disabled={locked}>
                  + 添加维度
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="small"
                  onClick={equalizeWeights}
                  disabled={locked}
                >
                  平均分配权重
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="small"
                  onClick={applyRecommended}
                  disabled={locked}
                >
                  应用推荐维度
                </Button>
              </div>

            </div>
          )}

          {!isEdit && (
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={autoPublish}
                onChange={(e) => setAutoPublish(e.target.checked)}
                className={styles.checkbox}
              />
              创建后立即发布（学生立即可见并作答）
            </label>
          )}
          {error && <div className={styles.error}>{error}</div>}
          <div className={styles.actions}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(isEdit ? `/jobs/${editJobId}` : '/jobs')}
            >
              取消
            </Button>
            <Button type="submit" loading={loading}>
              {isEdit ? '保存修改' : autoPublish ? '创建并发布' : '创建草稿'}
            </Button>
          </div>

        </form>
      </Card>
    </div>
  );
}

function QuestionEditor({
  jobType,
  questions,
  onChange,
  total,
  readOnly = false,
}: {
  jobType: JobType;
  questions: Question[];
  onChange: (q: Question[]) => void;
  total: number;
  readOnly?: boolean;
}) {

  const update = (idx: number, patch: Partial<Question>) => {
    onChange(questions.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  };

  const remove = (idx: number) => {
    onChange(questions.filter((_, i) => i !== idx));
  };

  const add = () => {
    const id = `q${questions.length + 1}`;
    const base: Question =
      jobType === 'fill_blank'
        ? { id, type: 'fill_blank', stem: '', options: [], answer: [''], score: 5 }
        : {
            id,
            type: 'single_choice',
            stem: '',
            options: ['', ''],
            answer: '',
            score: 5,
          };
    onChange([...questions, base]);
  };

  const changeType = (idx: number, t: QuestionType) => {
    const q = questions[idx];
    let answer: string | string[] = '';
    if (t === 'multiple_choice') {
      answer = typeof q.answer === 'string' && q.answer ? [q.answer] : [];
    } else if (t === 'single_choice') {
      answer = Array.isArray(q.answer) ? q.answer[0] ?? '' : q.answer;
    } else {
      answer = Array.isArray(q.answer) ? q.answer : q.answer ? [q.answer] : [''];
    }
    update(idx, { type: t, answer });
  };

  return (
    <div className={`${styles.questionSection} ${readOnly ? styles.readOnlySection : ''}`}>
      <div className={styles.dimensionsHeader}>
        <label className={styles.dimensionsTitle}>题目设置（自动批改）</label>

        <div className={styles.dimensionsMeta}>
          <span className={styles.metaOk}>共 {questions.length} 题</span>
          <span className={styles.metaOk}>满分合计 {total}</span>
        </div>
      </div>

      <div className={styles.questionList}>
        {questions.map((q, idx) => (
          <div key={q.id} className={styles.questionCard}>
            <div className={styles.questionHead}>
              <span className={styles.questionNo}>第 {idx + 1} 题</span>
              {jobType === 'choice' && (
                <div className={styles.typeToggle}>
                  <label className={styles.radioInline}>
                    <input
                      type="radio"
                      name={`type-${q.id}`}
                      checked={q.type === 'single_choice'}
                      onChange={() => changeType(idx, 'single_choice')}
                    />
                    单选
                  </label>
                  <label className={styles.radioInline}>
                    <input
                      type="radio"
                      name={`type-${q.id}`}
                      checked={q.type === 'multiple_choice'}
                      onChange={() => changeType(idx, 'multiple_choice')}
                    />
                    多选
                  </label>
                </div>
              )}
              <input
                type="number"
                min={0}
                className={styles.qScore}
                value={q.score}
                onChange={(e) => update(idx, { score: Number(e.target.value) })}
                aria-label="分值"
              />
              <span className={styles.qScoreLabel}>分</span>
              <button
                type="button"
                className={styles.dimRemove}
                onClick={() => remove(idx)}
                aria-label="删除题目"
                title="删除题目"
              >
                ×
              </button>
            </div>

            <textarea
              className={styles.textarea}
              value={q.stem}
              onChange={(e) => update(idx, { stem: e.target.value })}
              rows={2}
              placeholder="请输入题干"
              disabled={readOnly}
            />


            {q.type === 'fill_blank' ? (
              <div className={styles.blankList}>
                <span className={styles.fieldLabel}>参考答案（每空一个，多个可接受答案用 | 分隔）</span>
                {(q.answer as string[]).map((ans, bi) => (
                  <div key={bi} className={styles.blankItem}>
                    <span className={styles.blankNo}>第{bi + 1}空</span>
                    <input
                      type="text"
                      className={styles.dimInput}
                      value={ans}
                      onChange={(e) => {
                        const next = [...(q.answer as string[])];
                        next[bi] = e.target.value;
                        update(idx, { answer: next });
                      }}
                      placeholder="参考答案"
                    />
                    <button
                      type="button"
                      className={styles.dimRemove}
                      onClick={() => {
                        const next = (q.answer as string[]).filter((_, i) => i !== bi);
                        update(idx, { answer: next.length ? next : [''] });
                      }}
                      aria-label="删除空"
                      title="删除该空"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="secondary"
                  size="small"
                  onClick={() =>
                    update(idx, { answer: [...(q.answer as string[]), ''] })
                  }
                >
                  + 添加空
                </Button>
              </div>
            ) : (
              <div className={styles.optionList}>
                <span className={styles.fieldLabel}>选项与正确答案</span>
                {(q.options ?? []).map((opt, oi) => (
                  <div key={oi} className={styles.optionItem}>
                    <span className={styles.optionLetter}>{letterOf(oi)}</span>
                    <input
                      type="text"
                      className={styles.dimInput}
                      value={opt}
                      onChange={(e) => {
                        const next = [...(q.options ?? [])];
                        next[oi] = e.target.value;
                        update(idx, { options: next });
                      }}
                      placeholder={`选项 ${letterOf(oi)}`}
                    />
                    <label className={styles.radioInline}>
                      <input
                        type={
                          q.type === 'multiple_choice' ? 'checkbox' : 'radio'
                        }
                        name={`ans-${q.id}`}
                        checked={
                          q.type === 'multiple_choice'
                            ? Array.isArray(q.answer) &&
                              (q.answer as string[]).includes(letterOf(oi))
                            : q.answer === letterOf(oi)
                        }
                        onChange={() => {
                          if (q.type === 'multiple_choice') {
                            const cur = Array.isArray(q.answer)
                              ? [...(q.answer as string[])]
                              : [];
                            if (cur.includes(letterOf(oi))) {
                              update(idx, {
                                answer: cur.filter((x) => x !== letterOf(oi)),
                              });
                            } else {
                              update(idx, { answer: [...cur, letterOf(oi)] });
                            }
                          } else {
                            update(idx, { answer: letterOf(oi) });
                          }
                        }}
                      />
                      正确
                    </label>
                    <button
                      type="button"
                      className={styles.dimRemove}
                      onClick={() =>
                        update(idx, {
                          options: (q.options ?? []).filter((_, i) => i !== oi),
                        })
                      }
                      aria-label="删除选项"
                      title="删除选项"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="secondary"
                  size="small"
                  onClick={() =>
                    update(idx, { options: [...(q.options ?? []), ''] })
                  }
                >
                  + 添加选项
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className={styles.dimensionsActions}>
        <Button type="button" variant="secondary" size="small" onClick={add}>
          + 添加题目
        </Button>
      </div>
    </div>
  );
}
